import { forwardRef, useImperativeHandle, useRef } from 'react';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { Box, Typography } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import { EDITOR_HEIGHT } from './unlayerDesign';

export interface HtmlCodeEditorHandle {
  insertAtCursor: (text: string) => void;
}

interface HtmlCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const EDITOR_OPTIONS: editor.IStandaloneEditorConstructionOptions = {
  fontSize: 13,
  lineNumbers: 'on',
  minimap: { enabled: false },
  wordWrap: 'on',
  scrollBeyondLastLine: false,
  automaticLayout: true,
  tabSize: 2,
  formatOnPaste: true,
  formatOnType: true,
  folding: true,
  padding: { top: 10, bottom: 8 },
  renderLineHighlight: 'all',
  cursorBlinking: 'smooth',
};

const HtmlCodeEditor = forwardRef<HtmlCodeEditorHandle, HtmlCodeEditorProps>(({ value, onChange }, ref) => {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  useImperativeHandle(ref, () => ({
    insertAtCursor: (text: string) => {
      const instance = editorRef.current;
      if (!instance) return;
      const model = instance.getModel();
      const lastLine = model?.getLineCount() ?? 1;
      const { lineNumber, column } = instance.getPosition() ?? { lineNumber: lastLine, column: model?.getLineMaxColumn(lastLine) ?? 1 };
      instance.executeEdits('insert', [{
        range: { startLineNumber: lineNumber, startColumn: column, endLineNumber: lineNumber, endColumn: column },
        text,
        forceMoveMarkers: true,
      }]);
      instance.focus();
    },
  }), []);

  return (
    <Box sx={{ position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      {/* En-tête aux couleurs de l'éditeur Monaco (thème vs-dark) */}
      <Box sx={{ px: 2, py: 0.75, backgroundColor: '#1e1e1e', display: 'flex', alignItems: 'center', gap: 1 }}>
        <CodeIcon sx={{ fontSize: '0.9rem', color: '#9cdcfe' }} />
        <Typography variant="caption" sx={{ color: '#9cdcfe', fontFamily: theme => theme.typography.fontFamilyMono, fontSize: '0.75rem' }}>
          HTML
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 'auto' }}>
          Modifiez le HTML puis cliquez sur &quot;Builder visuel&quot; pour revenir dans Unlayer
        </Typography>
      </Box>
      <Editor
        height={EDITOR_HEIGHT}
        language="html"
        value={value}
        onChange={next => onChange(next ?? '')}
        onMount={instance => { editorRef.current = instance; }}
        theme="vs-dark"
        options={EDITOR_OPTIONS}
      />
    </Box>
  );
});

HtmlCodeEditor.displayName = 'HtmlCodeEditor';

export default HtmlCodeEditor;
